import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { AuthIntent, DEFAULT_AUTH_INTENT, resolveAuthIntent } from '../authentication/authIntent';

/**
 * What a call site may pass to `openSignIn`.
 *
 * `React.MouseEvent` is part of the type because several call sites are written
 * `onClick={openSignIn}` and therefore hand the handler their click event as the
 * first argument. Naming that here keeps those call sites type-checking, and
 * `resolveAuthIntent` treats anything that is not an intent as "none given".
 */
export type OpenSignInArg = AuthIntent | React.MouseEvent;

interface AuthUIContextType {
    /**
     * Opens the sign-in modal. Pass `'signUp'` from a registration CTA so the modal
     * opens on Create Account; omit it and the modal opens on Sign In as before.
     */
    openSignIn: (intent?: OpenSignInArg) => void;
    /**
     * The intent behind the most recent `openSignIn` call, read by the modal when
     * it mounts to pick its initial tab.
     */
    signInIntent: AuthIntent;
}

const AuthUIContext = createContext<AuthUIContextType>({
    openSignIn: () => {},
    signInIntent: DEFAULT_AUTH_INTENT,
});

export const AuthUIProvider: React.FC<React.PropsWithChildren<{ openSignIn: () => void }>> = ({ openSignIn, children }) => {
    const [signInIntent, setSignInIntent] = useState<AuthIntent>(DEFAULT_AUTH_INTENT);

    /*
     * The intent lives here rather than being threaded down as another prop.
     *
     * The `openSignIn` App passes in does two things — stashes `postAuthRedirect`
     * and flips the modal's visibility — and neither depends on which tab the modal
     * opens on. Holding the intent in the provider keeps it a single hop from the
     * button that was clicked to the modal that reads it, and leaves App and
     * NavigationComponent untouched.
     */
    const open = useCallback((intent?: OpenSignInArg) => {
        setSignInIntent(resolveAuthIntent(intent));
        openSignIn();
    }, [openSignIn]);

    const value = useMemo(() => ({ openSignIn: open, signInIntent }), [open, signInIntent]);

    return <AuthUIContext.Provider value={value}>{children}</AuthUIContext.Provider>;
};

export const useAuthUI = () => useContext(AuthUIContext);
